import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import Emitter from '@shared/services/main/events';
import EVENT_NAMES from '@shared/lib/constants/events';
import {useDetailFormContext} from '@shared/stores/base/detail-form-store';
import pluralize from 'pluralize';

/**
 * Gets the url parameter.
 *
 * @param      {string}   sParam  The s parameter
 * @return     {boolean}  The url parameter.
 */
export const getUrlParameter = (sParam) =>  {
    var sPageURL = window.location.search.substring(1),
        sURLVariables = sPageURL.split('&'),
        sParameterName,
        i;

    for (i = 0; i < sURLVariables.length; i++) {
        sParameterName = sURLVariables[i].split('=');

        if (sParameterName[0] === sParam) {
            return sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
        }
    }
    return false;
};

/**
 * Gets the base url.
 *
 * @return     {Promise}  The base url.
 */
export async function getBaseUrl() {
  return process.env.REACT_APP_API_BASE_URL;
}

/**
 * Gets the user token.
 *
 * @return     {Promise}  The user token.
 */
export async function getUserToken() {
  const token_name = process.env.REACT_APP_COOKIE_TOKEN || "token";
  var value = Cookies.get(token_name) || "";
  return value;
}


/**
 * Use entity collector to dynamically update your form entitiy values
 *
 * NOTE: If this hook is used in some entities that is implemented outside the
 * BaseDetailForm, it will still work but you won't have a data container just
 * like in the BaseDetailsForm, if you want to contain the data you should
 * either extend the BaseDetailForm or copy these methods for data container in
 * your parent component
 *
 * For Data container
 * - registerSyncListener
 * - removeSyncListener
 * - syncData
 *
 * @param      {Object}  [item={}]  The item
 * @param      {string}  flag_key   The flag key
 * @param      {<type>}  event_name_listener  The event name listener, a custom event that you can register in your DetailForm.js
 * @return     {Object}  state hooks and values
 */
export const useEntityCollector = (item = {}, flag_key) => {

    const state = useDetailFormContext();
  const [values, setValues] = useState({});
  const set = (name) => {
    return ({ target: { value } }) => {
      setValues(old_values => ({...old_values, [name]: value }));
    }
  }
  const setWithValue = (name, value) => {
    setValues(old_values => ({...old_values, [name]: value }));
  }
  /**
   * This will be the one to set the default but by using the flag to avoid
   * infiit loop between reRender when state is change
   *
   * This throws lint error: Line 66:6:  React Hook useEffect has missing
   * dependencies: 'flag_key' and 'item'. Either include them or remove the
   * dependency array. If 'setValues' needs the current value of 'item', you can
   * also switch to useReducer instead of useState and read 'item' in the
   * reducer  react-hooks/exhaustive-deps Line 66:7:  React Hook useEffect has a
   * complex expression in the dependency array. Extract it to a separate
   * variable so it can be statically checked
   * react-hooks/exhaustive-deps
   */
  // eslint-disable-next-line
  useEffect(() => {
    if (!flag_key) {
      console.error("Flag key is needed for useEntityCollector to avoid infinity!");
    } else {
      setValues(item);
    }
    // eslint-disable-next-line
    }, [JSON.stringify(item)]);

    const {event_entity_update = EVENT_NAMES.FORM.ENTITY_UPDATE} = state;
    Emitter.emit(event_entity_update, values);
  return {
    setValues,
    setWithValue,
    values,
    set,
  };
};


/**
 * Determines whether the specified check is falsy.
 *
 * Just checks everything if falsy in one-go, no need to next conditions on the
 * template
 *
 * @param {any} check The check
 * @return {boolean} True if the specified check is falsy, False otherwise.
 */
export const isFalsy = (check) => {
    if (!check) {
        return true;
    }
    if (typeof check == 'string') {
        if (!check.trim()) {
            return true;
        }
    }
    if (typeof check == 'object') {
        if (!Object.values(check).length) {
            return true;
        }
    }
    return false;
};

/**
 * Convert value as currency and round it to two decimal places
 *
 * @param      {number|string}  value   The value
 * @return     {string}         The new value
 */
export const asCurrency = (value) => {
    return (Math.round(value * 100) / 100).toFixed(2);
}

/**
 * Format underscore strings to capitalize words
 */
export const humanize = (str, glue='_') => {
  var i, frags = str.split(glue);
  for (i=0; i<frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
}

/**
 * Determines if callable.
 *
 * @param      {instance}  obj     The object
 * @param      {string}    method  The method
 * @return     {boolean}   True if callable, False otherwise.
 */
export const isCallable = (obj, method) => {
    if (isFalsy(obj) || isFalsy(method)) {
        return false;
    }
    return typeof obj[method] == 'function';
}

/**
 * Convert class name to item name
 *
 * @param  {class instance}  instance The instance
 * @return {string|object}   The item name
 */
export const toItemName = (instance)  => {
    let item_name = typeof instance === 'string'
        ? instance
        : instance.constructor.name;
    item_name = item_name.replace('Service', '');
    item_name = item_name.replace(/([a-z])([A-Z])/g, '$1 $2');
    item_name = pluralize.singular(item_name);
    return item_name;
}

/**
 * Determines if electron.
 * Excludes automation/testing browsers (like MCP browser tools).
 *
 * @return {boolean}  True if electron, False otherwise.
 */
export const isElectron = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Exclude automation/testing browsers (MCP browser, Puppeteer, etc.)
    // These often have specific indicators in the user agent or navigator properties
    if (userAgent.includes('headlesschrome') || 
        userAgent.includes('puppeteer') ||
        userAgent.includes('automation') ||
        userAgent.includes('webdriver') ||
        userAgent.includes('selenium') ||
        userAgent.includes('playwright') ||
        userAgent.includes('chromium') && !userAgent.includes(' electron/') ||
        navigator.webdriver === true ||
        (typeof navigator.webdriver !== 'undefined' && navigator.webdriver)) {
        return false;
    }
    
    // Only return true if we explicitly see " electron/" in the user agent
    return userAgent.indexOf(' electron/')>0? true: false;
}

/**
 * Determines if the user is on a mobile device based on user agent.
 * This is more reliable than viewport width for detecting actual mobile devices.
 *
 * @return {boolean}  True if mobile device, False otherwise.
 */
export const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(userAgent);
}

/**
 * TODO: below migrated codes needs docus
 * -
 */
export const sleep = ms => new Promise(r => setTimeout(r, ms));

export const pad = (num, size) => {
    var s = num + "";
    while (s.length < size)
        s = "0" + s;
    return s;
}

export const printOdd = (odd, odds_format = "decimal") => {
    if (odds_format === "decimal") {
        return parseFloat(odd).toFixed(2);
    }
    if (odds_format === "fractional") {
        return odd;
    }
    if (odds_format === "american") {
        var floatOdd = parseFloat(odd);
        if (floatOdd < 2) {
            return "-" + Math.floor(-100 / (floatOdd-1)).toString();
        }
        if (floatOdd >= 2) {
            return "+" + Math.floor(((floatOdd - 1) * 100)).toString();
        }
    }
}